import { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { PageHeader, SectionCard, Loader, EmptyState, KpiCard } from "@/components/common";
import { formatINR, formatDate, timeAgo } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Users, Plus, Crown, Repeat, AlertCircle, Loader2, Search } from "lucide-react";
import { toast } from "sonner";

const SEGMENT = {
  high_value: { label: "High Value", cls: "bg-primary/10 text-primary", icon: Crown },
  repeat: { label: "Repeat", cls: "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]", icon: Repeat },
  at_risk: { label: "At Risk", cls: "bg-destructive/10 text-destructive", icon: AlertCircle },
  regular: { label: "Regular", cls: "bg-secondary text-secondary-foreground", icon: Users },
};

export default function Customers() {
  const [customers, setCustomers] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = () => api.get("/customers").then((r) => setCustomers(r.data));
  useEffect(() => { load(); }, []);
  if (!customers) return <Loader label="Loading customers…" />;

  const filtered = customers.filter((c) => `${c.name} ${c.phone}`.toLowerCase().includes(q.toLowerCase()));
  const sorted = [...filtered].sort((a, b) => b.total_spent - a.total_spent);
  const totalCLV = customers.reduce((s, c) => s + c.total_spent, 0);
  const outstanding = customers.reduce((s, c) => s + (c.outstanding || 0), 0);
  const highValue = customers.filter((c) => c.segment === "high_value").length;

  const add = async () => {
    setSaving(true);
    try { await api.post("/customers", form); toast.success("Customer added"); setOpen(false); setForm({ name: "", phone: "", email: "" }); load(); }
    catch { toast.error("Failed"); } finally { setSaving(false); }
  };

  const openDetail = async (id) => {
    const r = await api.get(`/customers/${id}`);
    setDetail(r.data);
  };

  return (
    <div>
      <PageHeader title="Customers" subtitle="Relationships, lifetime value and who needs a nudge.">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-customer-btn" className="font-semibold"><Plus className="h-4 w-4 mr-1.5" /> Add Customer</Button></DialogTrigger>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle className="font-heading">Add Customer</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input data-testid="customer-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className="mt-1" /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="mt-1" /></div>
            </div>
            <DialogFooter><Button onClick={add} disabled={saving || !form.name} data-testid="save-customer-btn" className="w-full">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard testid="cust-total" index={0} label="Total Customers" value={customers.length} icon={Users} />
        <KpiCard testid="cust-clv" index={1} label="Lifetime Value" value={formatINR(totalCLV, { compact: true })} icon={Crown} accent="success" />
        <KpiCard testid="cust-high" index={2} label="High Value" value={highValue} icon={Crown} accent="primary" />
        <KpiCard testid="cust-outstanding" index={3} label="Outstanding" value={formatINR(outstanding, { compact: true })} icon={AlertCircle} accent="warning" />
      </div>

      <div className="relative max-w-xs mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input data-testid="customer-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers…" className="pl-9" />
      </div>

      <SectionCard testid="customers-table">
        {sorted.length === 0 ? <EmptyState icon={Users} title="No customers" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="text-left font-semibold py-3">Customer</th>
                <th className="text-left font-semibold py-3 hidden md:table-cell">Segment</th>
                <th className="text-right font-semibold py-3">Spent</th>
                <th className="text-right font-semibold py-3 hidden sm:table-cell">Orders</th>
                <th className="text-right font-semibold py-3 hidden lg:table-cell">Outstanding</th>
                <th className="text-left font-semibold py-3 hidden md:table-cell">Last Purchase</th>
                <th></th>
              </tr></thead>
              <tbody>
                {sorted.map((c) => {
                  const seg = SEGMENT[c.segment];
                  return (
                    <tr key={c.id} className="border-b border-border/60 hover:bg-secondary/40 transition-colors cursor-pointer" onClick={() => openDetail(c.id)} data-testid={`customer-row-${c.id}`}>
                      <td className="py-3"><div className="font-medium">{c.name}</div><div className="text-xs text-muted-foreground">{c.phone}</div></td>
                      <td className="py-3 hidden md:table-cell"><span className={`text-[11px] font-semibold px-2 py-1 rounded-full ${seg.cls}`}>{seg.label}</span></td>
                      <td className="py-3 text-right font-semibold tabular-nums">{formatINR(c.total_spent, { compact: true })}</td>
                      <td className="py-3 text-right hidden sm:table-cell">{c.purchase_count}</td>
                      <td className="py-3 text-right hidden lg:table-cell tabular-nums">{c.outstanding ? <span className="text-[hsl(var(--warning))] font-medium">{formatINR(c.outstanding, { compact: true })}</span> : "—"}</td>
                      <td className="py-3 hidden md:table-cell text-muted-foreground">{c.last_purchase ? timeAgo(c.last_purchase) : "Never"}</td>
                      <td className="py-3 text-right"><Button size="sm" variant="ghost">View</Button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{detail?.name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg bg-secondary p-3"><div className="text-xs text-muted-foreground">Lifetime Value</div><div className="font-heading text-lg font-bold">{formatINR(detail.total_spent)}</div></div>
                <div className="rounded-lg bg-secondary p-3"><div className="text-xs text-muted-foreground">Outstanding</div><div className="font-heading text-lg font-bold text-[hsl(var(--warning))]">{formatINR(detail.outstanding || 0)}</div></div>
                <div className="text-muted-foreground">Phone: <span className="text-foreground">{detail.phone || "—"}</span></div>
                <div className="text-muted-foreground">Orders: <span className="text-foreground">{detail.purchase_count}</span></div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 font-semibold">Purchase History</div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {detail.invoices?.length ? detail.invoices.map((inv) => (
                    <div key={inv.id} className="flex justify-between text-sm border-b border-border/50 pb-2">
                      <div><div className="font-medium">{inv.invoice_number}</div><div className="text-xs text-muted-foreground">{formatDate(inv.created_at)}</div></div>
                      <div className="text-right"><div className="font-semibold">{formatINR(inv.total)}</div><Badge variant="secondary" className="uppercase text-[9px]">{inv.payment_method}</Badge></div>
                    </div>
                  )) : <EmptyState icon={Users} title="No purchases yet" />}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
